// src/pages/Perfil/MisLicencias.js
// Panel de autoservicio para el empleado: ver saldo, solicitar licencia, ver sus solicitudes.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, Chip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PrintIcon from '@mui/icons-material/Print';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AdjuntosSolicitud from '../../components/AdjuntosSolicitud';
import FechaBloqueada from '../../components/FechaBloqueada';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MiCalendario from './MiCalendario';
import { nodeApiPath } from '../../utils/paths';
import { triggerSessionExpired } from '../../api/endpoints';
import { useNotification } from '../../contexts/NotificationContext';

const PRIMARY = '#0277bd';
const PRIMARY_DARK = '#01579b';
const BR = 4;

async function jf(path, options = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No hay token'); }
    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            'Accept': 'application/json',
            ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

const fmtDate = (d) => { if (!d) return '-'; const dt = new Date(d); return dt.toLocaleDateString('es-AR'); };

async function descargarConstanciaPDF(persona, solicitud) {
    const fmt = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
    const hoy = new Date().toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    const html = `
        <div style="text-align:center;border-bottom:2px solid #0277bd;padding-bottom:10px;margin-bottom:25px">
          <div style="font-size:14pt;font-weight:bold;color:#0277bd">FISCALÍA DE ESTADO</div>
          <div style="font-size:10pt;color:#666">Provincia de San Juan</div>
        </div>
        <h2 style="text-align:center;font-size:13pt;text-decoration:underline;margin:25px 0">CONSTANCIA DE LICENCIA</h2>
        <p style="text-align:justify;font-size:11pt;line-height:1.7;text-indent:30px">
          Por la presente se deja constancia que el/la agente
          <strong>${(persona.Apellido || '').toUpperCase()}, ${(persona.Nombre || '').toUpperCase()}</strong>,
          DNI N° <strong>${persona.Id || '—'}</strong>, CUIL N° <strong>${persona.CUIL || '—'}</strong>,
          ha sido autorizado/a a hacer uso de licencia en el marco del trámite N° ${solicitud.Id || '—'},
          ${solicitud.Articulo ? `bajo el <strong>artículo ${solicitud.Articulo}</strong>,` : ''}
          por el período comprendido entre el <strong>${fmt(solicitud.Fecha_Desde)}</strong> y el <strong>${fmt(solicitud.Fecha_Hasta)}</strong>,
          inclusive, totalizando <strong>${solicitud.Dias_Habiles || 0} día${solicitud.Dias_Habiles === 1 ? '' : 's'} hábiles</strong>.
        </p>
        ${solicitud.Revisado_Por ? `<p style="font-size:10pt;color:#555">Aprobado por: <strong>${solicitud.Revisado_Por}</strong> el ${fmt(solicitud.Fecha_Revision)}.</p>` : ''}
        <p style="text-align:justify;font-size:11pt;line-height:1.7;text-indent:30px">Se extiende la presente a pedido del/la interesado/a.</p>
        <div style="text-align:right;font-size:11pt;margin-top:35px">San Juan, ${hoy}.</div>
        <div style="display:flex;justify-content:space-around;margin-top:90px">
          <div style="text-align:center"><div style="border-top:1px solid #333;width:220px;padding-top:4px;font-size:10pt">Departamento Personal</div></div>
          <div style="text-align:center"><div style="border-top:1px solid #333;width:220px;padding-top:4px;font-size:10pt">Fiscal de Estado</div></div>
        </div>`;
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    const res = await fetch(nodeApiPath('pdf/from-html'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ html, filename: `Constancia_Licencia_${persona.Apellido || ''}_${solicitud.Id}`, orientation: 'portrait' }),
    });
    if (!res.ok) throw new Error('Error generando PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Constancia_Licencia_${solicitud.Id}.pdf`; document.body.appendChild(a);
    a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const MisLicencias = () => {
    const { showNotification } = useNotification();
    const notify = useCallback((msg, severity = 'success') => {
        showNotification(msg, severity);
    }, [showNotification]);

    const [resumen, setResumen] = useState(null);
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [artLic, setArtLic] = useState([]);
    const [licTipos, setLicTipos] = useState([]);
    const [directores, setDirectores] = useState([]);

    // Adjuntos
    const [adjDlg, setAdjDlg] = useState({ open: false, solicitud: null });

    // Nueva solicitud
    const [nvaDlg, setNvaDlg] = useState({ open: false, saving: false });
    const [nva, setNva] = useState({
        Tipo: 'LICENCIA', Id_Tipo_Licencia: '', Id_Licencias_Articulo: '',
        Fecha_Desde: '', Fecha_Hasta: '', Id_Director: '',
        Observaciones: '', Ano_Vacaciones: new Date().getFullYear(),
    });
    const [nvaArchivo, setNvaArchivo] = useState(null); // File para adjuntar al crear (carta médica)
    const [vistaTab, setVistaTab] = useState('listado');
    const [diasCalc, setDiasCalc] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                jf('personal/mi/resumen'),
                jf('personal/mi/solicitudes'),
            ]);
            if (r1.ok) setResumen(r1);
            if (r2.ok) setSolicitudes(r2.solicitudes || []);
        } catch (e) { notify('Error al cargar datos: ' + e.message, 'error'); }
        setLoading(false);
    }, [notify]);

    useEffect(() => {
        cargar();
        jf('personal/articulos-licencias').then(r => setArtLic(r.articulos || [])).catch(() => { });
        jf('admin/catalogos/licencias-tipo').then(r => {
            if (r.ok) {
                // Excluir "Vacaciones" del dropdown de Tipo de Licencia (se gestiona por separado)
                const tipos = (r.datos || []).filter(t => t.Id > 0 && !(t.Tipo || '').toUpperCase().includes('VACACION'));
                setLicTipos(tipos);
            }
        }).catch(() => { });
        // Autodetect de jefes del área del empleado (nuevo flujo)
        jf('personal/roles/mis-jefes').then(r => { if (r.ok) setDirectores(r.items || []); }).catch(() => { });
    }, [cargar]);

    // Calcular días hábiles cuando cambia el rango
    useEffect(() => {
        if (nva.Fecha_Desde && nva.Fecha_Hasta) {
            jf(`personal/solicitudes/dias-habiles?desde=${nva.Fecha_Desde}&hasta=${nva.Fecha_Hasta}`)
                .then(r => { if (r.ok) setDiasCalc(r.dias); })
                .catch(() => setDiasCalc(null));
        } else setDiasCalc(null);
    }, [nva.Fecha_Desde, nva.Fecha_Hasta]);

    const handleEnviar = async () => {
        if (!nva.Fecha_Desde) { notify('Complete la fecha', 'warning'); return; }
        if (nva.Tipo !== 'ART42' && !nva.Fecha_Hasta) { notify('Complete la fecha Hasta', 'warning'); return; }
        if (nva.Tipo === 'LICENCIA' && !nva.Id_Licencias_Articulo) { notify('Seleccione un artículo', 'warning'); return; }
        if (nva.Tipo === 'VACACIONES' && !nva.Id_Director) { notify('Seleccione un director/subdirector', 'warning'); return; }

        setNvaDlg(p => ({ ...p, saving: true }));
        try {
            // Art 42: crear 1 solicitud por día (pueden ser 2 días puntuales, no rango)
            if (nva.Tipo === 'ART42') {
                const dias = [nva.Fecha_Desde];
                if (nva.Fecha_Hasta && nva.Fecha_Hasta !== nva.Fecha_Desde) dias.push(nva.Fecha_Hasta);
                let ok = true;
                for (const dia of dias) {
                    const r = await jf('personal/mi/solicitudes', {
                        method: 'POST',
                        body: JSON.stringify({ ...nva, Fecha_Desde: dia, Fecha_Hasta: dia }),
                    });
                    if (!r.ok) { notify(r.mensaje || 'Error', 'error'); ok = false; break; }
                }
                if (ok) {
                    notify(dias.length === 1 ? 'Art. 42 registrado (1 día)' : 'Art. 42 registrado (2 días)');
                    setNvaDlg({ open: false, saving: false });
                    setNva({ Tipo: 'LICENCIA', Id_Tipo_Licencia: '', Id_Licencias_Articulo: '', Fecha_Desde: '', Fecha_Hasta: '', Id_Director: '', Observaciones: '', Ano_Vacaciones: new Date().getFullYear() });
                    cargar();
                }
            } else {
                // Resto de tipos: solicitud normal con rango de fechas
                const r = await jf('personal/mi/solicitudes', { method: 'POST', body: JSON.stringify(nva) });
                if (r.ok) {
                    if (nvaArchivo && r.id) {
                        try {
                            const fd = new FormData();
                            fd.append('file', nvaArchivo);
                            fd.append('tipo', 'JUNTA_MEDICA');
                            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
                            await fetch(nodeApiPath(`personal/solicitudes/${r.id}/adjuntos`), {
                                method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` },
                            });
                        } catch { }
                    }
                    notify('Solicitud enviada. Recibirás una notificación cuando sea revisada.');
                    setNvaDlg({ open: false, saving: false });
                    setNva({ Tipo: 'LICENCIA', Id_Tipo_Licencia: '', Id_Licencias_Articulo: '', Fecha_Desde: '', Fecha_Hasta: '', Id_Director: '', Observaciones: '', Ano_Vacaciones: new Date().getFullYear() });
                    setNvaArchivo(null);
                    cargar();
                } else notify(r.mensaje || 'Error', 'error');
            }
        } catch (e) { notify('Error: ' + e.message, 'error'); }
        setNvaDlg(p => ({ ...p, saving: false }));
    };

    const articulosFiltrados = nva.Id_Tipo_Licencia
        ? artLic.filter(a => String(a.Id_Tipo_Licencia) === String(nva.Id_Tipo_Licencia))
        : [];

    const estadoChip = (estado) => {
        const pend = { bg: '#fff3e0', color: '#e65100', icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> };
        const rej = { bg: '#ffebee', color: '#c62828', icon: <CancelIcon sx={{ fontSize: 14 }} /> };
        const ok = { bg: '#e8f5e9', color: '#2e7d32', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
        const map = {
            PENDIENTE: pend, EN_REVISION: { ...pend, bg: '#e3f2fd', color: '#1565c0' },
            APROBADA: ok, FINALIZADA: ok, RECHAZADA: rej,
            PENDIENTE_DIRECTOR: pend, PENDIENTE_RRHH: pend,
            PENDIENTE_DIR_ADMIN: pend, PENDIENTE_JURIDICO: pend, PENDIENTE_AUTORIDAD: pend,
            EN_LOTE: pend,
            RECHAZADA_DIRECTOR: rej, RECHAZADA_RRHH: rej, RECHAZADA_DIR_ADMIN: rej,
            RECHAZADA_JURIDICO: rej, RECHAZADA_AUTORIDAD: rej,
        };
        const labels = {
            PENDIENTE_DIRECTOR: 'Pend. Director',
            PENDIENTE_RRHH: 'Pend. RRHH',
            PENDIENTE_DIR_ADMIN: 'Pend. Dir. Adm.',
            PENDIENTE_JURIDICO: 'Pend. Jurídico',
            PENDIENTE_AUTORIDAD: 'Pend. Autoridad',
            RECHAZADA_DIRECTOR: 'Rechazada (Director)',
            RECHAZADA_RRHH: 'Rechazada (RRHH)',
            RECHAZADA_DIR_ADMIN: 'Rechazada (Dir. Adm.)',
            RECHAZADA_JURIDICO: 'Rechazada (Jurídico)',
            RECHAZADA_AUTORIDAD: 'Rechazada (Autoridad)',
        };
        const s = map[estado] || { bg: '#f5f5f5', color: '#666' };
        return <Chip label={labels[estado] || estado} size="small" icon={s.icon} sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.7rem', borderRadius: BR }} />;
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;

    if (!resumen) return <Alert severity="info" sx={{ borderRadius: BR }}>Tu usuario no tiene un legajo asociado. Solicita al administrador que lo vincule.</Alert>;

    const saldoVigente = (resumen.vacaciones?.saldos || [])
        .map(s => ({ ...s, disp: (s.Dias_Correspondientes || 0) - (s.Dias_Usados || 0) }))
        .filter(s => s.disp > 0);

    return (
        <Box>
            {/* Resumen */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, borderRadius: BR, borderLeft: `4px solid ${PRIMARY}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BeachAccessIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Vacaciones disponibles</Typography>
                    </Box>
                    {saldoVigente.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#999' }}>Sin saldo vigente</Typography>
                    ) : saldoVigente.map(s => (
                        <Box key={s.Ano} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', py: 0.25 }}>
                            <span>Período {s.Ano}</span>
                            <strong>{s.disp} / {s.Dias_Correspondientes} días</strong>
                        </Box>
                    ))}
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
                        Antigüedad: {resumen.vacaciones?.anosAntiguedad || 0} años · Corresponden {resumen.vacaciones?.diasCorresponden || 0} días/año
                    </Typography>
                </Paper>

                <Paper sx={{ p: 2, borderRadius: BR, borderLeft: '4px solid #6a1b9a' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EventBusyIcon sx={{ color: '#6a1b9a', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Art. 42 ({resumen.ano})</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span>Disponibles año:</span>
                        <strong>{resumen.art42?.disponiblesAno || 0} / 6</strong>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
                        Usados este año: {resumen.art42?.usadosAno || 0} · Máx 2 por mes
                    </Typography>
                </Paper>

                <Paper sx={{ p: 2, borderRadius: BR, borderLeft: '4px solid #e65100', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <Button variant="contained" size="large" startIcon={<AddIcon />}
                        onClick={() => setNvaDlg({ open: true, saving: false })}
                        sx={{ bgcolor: PRIMARY, borderRadius: BR, textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK } }}>
                        Solicitar Licencia
                    </Button>
                    <Typography variant="caption" sx={{ color: '#666', mt: 1, textAlign: 'center' }}>
                        La solicitud irá al director que elijas
                    </Typography>
                </Paper>
            </Box>

            {/* Listado de solicitudes con tabs */}
            {/* Vista toggle: Listado o Calendario */}
            <Paper sx={{ borderRadius: BR, mb: 2, overflow: 'hidden' }}>
                <Tabs value={vistaTab} onChange={(_, v) => setVistaTab(v)}
                    sx={{ borderBottom: '1px solid #e0e0e0', '& .MuiTab-root': { textTransform: 'none', fontSize: '0.85rem', minHeight: 42 },
                        '& .Mui-selected': { color: PRIMARY }, '& .MuiTabs-indicator': { bgcolor: PRIMARY } }}>
                    <Tab icon={<FormatListBulletedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Listado" value="listado" />
                    <Tab icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Calendario" value="calendario" />
                </Tabs>
            </Paper>

            {vistaTab === 'calendario' && (
                <Paper sx={{ p: 2, borderRadius: BR, mb: 2 }}>
                    <MiCalendario resumen={resumen} notify={notify} onSolicitudCreada={cargar} />
                </Paper>
            )}
            {vistaTab === 'listado' && (
                <MisSolicitudesTabbed solicitudes={solicitudes} persona={resumen?.persona || {}} estadoChip={estadoChip}
                    notify={notify} adjDlg={adjDlg} setAdjDlg={setAdjDlg} />
            )}

            {/* Dialog: Adjuntos */}
            <AdjuntosSolicitud
                open={adjDlg.open}
                idSolicitud={adjDlg.solicitud?.Id}
                solicitud={adjDlg.solicitud}
                onClose={() => setAdjDlg({ open: false, solicitud: null })}
                puedeSubir={true}
                puedeEliminar={adjDlg.solicitud?.Estado?.startsWith('PENDIENTE')}
                notify={notify}
            />

            {/* Dialog: Nueva solicitud */}
            <Dialog open={nvaDlg.open} onClose={() => setNvaDlg({ open: false, saving: false })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
                <DialogTitle sx={{ bgcolor: '#e3f2fd', color: PRIMARY, fontSize: '1rem', fontWeight: 700 }}>
                    Nueva solicitud de licencia
                </DialogTitle>
                <DialogContent sx={{ pt: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField select label="Tipo" value={nva.Tipo} size="small" fullWidth
                            onChange={e => setNva(p => ({ ...p, Tipo: e.target.value, Id_Tipo_Licencia: '', Id_Licencias_Articulo: '' }))}>
                            <MenuItem value="LICENCIA">Licencia (con artículo)</MenuItem>
                            <MenuItem value="VACACIONES">Vacaciones</MenuItem>
                            <MenuItem value="ART42">Art. 42 (día especial)</MenuItem>
                            <MenuItem value="CARTA_MEDICA">Carta Médica</MenuItem>
                        </TextField>

                        {nva.Tipo === 'LICENCIA' && (
                            <>
                                <TextField select label="Tipo de Licencia" value={nva.Id_Tipo_Licencia} size="small" fullWidth
                                    onChange={e => setNva(p => ({ ...p, Id_Tipo_Licencia: e.target.value, Id_Licencias_Articulo: '' }))}>
                                    {licTipos.map(t => <MenuItem key={t.Id} value={t.Id}>{t.Tipo}</MenuItem>)}
                                </TextField>
                                <TextField select label="Artículo / Inciso" value={nva.Id_Licencias_Articulo} size="small" fullWidth
                                    disabled={!nva.Id_Tipo_Licencia}
                                    onChange={e => setNva(p => ({ ...p, Id_Licencias_Articulo: e.target.value }))}
                                    SelectProps={{
                                        renderValue: (v) => {
                                            const a = articulosFiltrados.find(x => String(x.Id) === String(v));
                                            if (!a) return '';
                                            const txt = (a.Descripcion || '').replace(/[\r\n\t]+/g, ' ').trim();
                                            return txt.length > 80 ? txt.substring(0, 80) + '…' : txt;
                                        },
                                    }}>
                                    {articulosFiltrados.map(a => {
                                        const txt = (a.Descripcion || '').replace(/[\r\n\t]+/g, ' ').trim();
                                        const corto = txt.length > 120 ? txt.substring(0, 120) + '…' : txt;
                                        return (
                                            <MenuItem key={a.Id} value={a.Id} sx={{ whiteSpace: 'normal', py: 1 }}>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                                        Art. {a.Id}{a.Ley ? ` · ${a.Ley}` : ''}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                                        {corto}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </>
                        )}

                        {nva.Tipo === 'VACACIONES' && (
                            <TextField select label="Período" value={nva.Ano_Vacaciones} size="small" fullWidth
                                onChange={e => setNva(p => ({ ...p, Ano_Vacaciones: parseInt(e.target.value) }))}>
                                {saldoVigente.map(s => <MenuItem key={s.Ano} value={s.Ano}>{s.Ano} — {s.disp} días disponibles</MenuItem>)}
                                {saldoVigente.length === 0 && <MenuItem disabled value="">Sin saldo vigente</MenuItem>}
                            </TextField>
                        )}

                        {nva.Tipo === 'ART42' ? (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FechaBloqueada label="Día 1 *" value={nva.Fecha_Desde}
                                    onChange={v => setNva(p => ({ ...p, Fecha_Desde: v, Fecha_Hasta: p.Fecha_Hasta || v }))} />
                                <FechaBloqueada label="Día 2 (opcional)" value={nva.Fecha_Hasta !== nva.Fecha_Desde ? nva.Fecha_Hasta : ''}
                                    onChange={v => setNva(p => ({ ...p, Fecha_Hasta: v || p.Fecha_Desde }))} />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FechaBloqueada label="Desde *" value={nva.Fecha_Desde}
                                    onChange={v => setNva(p => ({ ...p, Fecha_Desde: v }))} />
                                <FechaBloqueada label="Hasta *" value={nva.Fecha_Hasta}
                                    minDate={nva.Fecha_Desde || undefined}
                                    onChange={v => setNva(p => ({ ...p, Fecha_Hasta: v }))} />
                            </Box>
                        )}
                        {diasCalc !== null && nva.Tipo !== 'ART42' && (
                            <Typography variant="caption" sx={{ color: PRIMARY, fontWeight: 600 }}>
                                {diasCalc} día{diasCalc !== 1 ? 's' : ''} hábil{diasCalc !== 1 ? 'es' : ''}
                            </Typography>
                        )}

                        {nva.Tipo === 'VACACIONES' && (
                            <Box>
                                <TextField select label="Director/Subdirector de tu área"
                                    value={nva.Id_Director} size="small" fullWidth required
                                    onChange={e => setNva(p => ({ ...p, Id_Director: e.target.value }))}
                                    helperText={directores.length === 0 ? 'No se detectó jefe automáticamente' : `Se autodetectaron ${directores.length} jefe(s) de tu área — podés cambiarlo si querés`}>
                                    {directores.map(d => (
                                        <MenuItem key={d.Id} value={d.Id}>
                                            {d.Apellido}, {d.Nombre} — {d.Rol === 'SUBDIRECTOR' ? 'Subdirector' : 'Director'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}

                        <TextField label="Observaciones (opcional)" value={nva.Observaciones} multiline rows={2} size="small" fullWidth
                            onChange={e => setNva(p => ({ ...p, Observaciones: e.target.value }))} />

                        {nva.Tipo === 'ART42' && (
                            <Alert severity="info" sx={{ borderRadius: BR, fontSize: '0.8rem' }}>
                                Los Art. 42 se envían a RRHH y luego a Secretaría General. Tenés {resumen.art42?.disponiblesAno || 0} días disponibles este año (máx 2 por mes).
                            </Alert>
                        )}
                        {nva.Tipo === 'LICENCIA' && (
                            <Alert severity="info" sx={{ borderRadius: BR, fontSize: '0.8rem' }}>
                                La solicitud irá a RRHH para evaluación, luego Dirección Administrativa, Jurídico y Secretaría General.
                            </Alert>
                        )}
                        {nva.Tipo === 'VACACIONES' && (
                            <Alert severity="info" sx={{ borderRadius: BR, fontSize: '0.8rem' }}>
                                Flujo: tu director aprueba → RRHH genera disposición → Secretaría General finaliza.
                            </Alert>
                        )}
                        {nva.Tipo === 'CARTA_MEDICA' && (
                            <Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} sx={{
                                        textTransform: 'none', borderRadius: BR, flex: 1,
                                        borderColor: nvaArchivo ? '#2e7d32' : '#1565c0',
                                        color: nvaArchivo ? '#2e7d32' : '#1565c0', justifyContent: 'flex-start' }}>
                                        {nvaArchivo ? `📎 ${nvaArchivo.name}` : 'Adjuntar PDF de junta médica (opcional)'}
                                        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={e => setNvaArchivo(e.target.files?.[0] || null)} />
                                    </Button>
                                    {nvaArchivo && (
                                        <IconButton size="small" onClick={() => setNvaArchivo(null)} sx={{ color: '#c62828' }}>
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <Alert severity="info" sx={{ borderRadius: BR, fontSize: '0.8rem', mt: 1 }}>
                                    Si no adjuntás el PDF ahora, RRHH o vos pueden subirlo después desde el ícono 📎.
                                    Flujo: RRHH arma lote → Dir. Administrativa → Jurídico → RRHH → Secretaría General.
                                </Alert>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNvaDlg({ open: false, saving: false })} sx={{ textTransform: 'none' }}>Cancelar</Button>
                    <Button variant="contained" onClick={handleEnviar} disabled={nvaDlg.saving}
                        startIcon={nvaDlg.saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AddIcon />}
                        sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: PRIMARY_DARK } }}>
                        {nvaDlg.saving ? 'Enviando...' : 'Enviar solicitud'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Sub-componente: listado con tabs por estado
function MisSolicitudesTabbed({ solicitudes, persona, estadoChip, notify, adjDlg, setAdjDlg }) {
    const [tab, setTab] = useState('pendientes');
    const isPend = (e) => e && (e.startsWith('PENDIENTE') || e === 'EN_LOTE' || e === 'EN_REVISION');
    const isOk = (e) => e === 'APROBADA' || e === 'FINALIZADA';
    const isRej = (e) => e && (e.startsWith('RECHAZADA') || e === 'RECHAZADA');

    const filtradas = solicitudes.filter(s => {
        if (tab === 'pendientes') return isPend(s.Estado);
        if (tab === 'aprobadas') return isOk(s.Estado);
        if (tab === 'rechazadas') return isRej(s.Estado);
        return true;
    });

    return (
        <Paper sx={{ borderRadius: BR, mb: 2, overflow: 'hidden' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: '1px solid #e0e0e0', '& .MuiTab-root': { textTransform: 'none', fontSize: '0.85rem', minHeight: 38 },
                    '& .Mui-selected': { color: PRIMARY }, '& .MuiTabs-indicator': { bgcolor: PRIMARY } }}>
                <Tab value="pendientes" label={`Pendientes (${solicitudes.filter(s => isPend(s.Estado)).length})`} />
                <Tab value="aprobadas" label={`Aprobadas (${solicitudes.filter(s => isOk(s.Estado)).length})`} />
                <Tab value="rechazadas" label={`Rechazadas (${solicitudes.filter(s => isRej(s.Estado)).length})`} />
                <Tab value="todas" label={`Todas (${solicitudes.length})`} />
            </Tabs>
            <Box sx={{ p: 1.5 }}>
                {filtradas.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#999', py: 2, textAlign: 'center' }}>Sin solicitudes en esta categoría.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Tipo', 'Artículo', 'Desde', 'Hasta', 'Días', 'Estado', 'Revisión', ''].map(h =>
                                        <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#fafafa', borderBottom: `2px solid ${PRIMARY}`, fontSize: '0.8rem' }}>{h}</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtradas.map(s => {
                                    const artCorto = (s.Articulo || '-').replace(/[\r\n\t]+/g, ' ');
                                    return (
                                        <TableRow key={s.Id} hover>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>
                                                <Chip label={s.Tipo} size="small" sx={{
                                                    bgcolor: s.Tipo === 'VACACIONES' ? '#e0f2f1' : s.Tipo === 'ART42' ? '#f3e5f5' : s.Tipo === 'CARTA_MEDICA' ? '#fff3e0' : '#e3f2fd',
                                                    color: s.Tipo === 'VACACIONES' ? '#00695c' : s.Tipo === 'ART42' ? '#6a1b9a' : s.Tipo === 'CARTA_MEDICA' ? '#bf360c' : '#1565c0',
                                                    fontSize: '0.65rem', borderRadius: BR,
                                                }} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.78rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{artCorto}</TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(s.Fecha_Desde)}</TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(s.Fecha_Hasta)}</TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{s.Dias_Habiles}</TableCell>
                                            <TableCell>{estadoChip(s.Estado)}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', color: '#666' }}>
                                                {s.Fecha_Revision ? `${fmtDate(s.Fecha_Revision)} (${s.Revisado_Por || '-'})` : '-'}
                                                {s.Motivo_Rechazo && <Typography variant="caption" sx={{ display: 'block', color: '#c62828' }}>{s.Motivo_Rechazo}</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', py: 0.25, whiteSpace: 'nowrap' }}>
                                                {(s.Tipo === 'LICENCIA' || s.Tipo === 'CARTA_MEDICA') && (
                                                    <Tooltip title="Adjuntos">
                                                        <IconButton size="small" sx={{ color: '#1565c0' }} onClick={() => setAdjDlg({ open: true, solicitud: s })}>
                                                            <AttachFileIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {isOk(s.Estado) && (
                                                    <Tooltip title="Descargar constancia PDF">
                                                        <IconButton size="small" sx={{ color: '#c62828' }} onClick={async () => {
                                                            try { await descargarConstanciaPDF(persona, s); }
                                                            catch { notify('Error', 'error'); }
                                                        }}>
                                                            <PrintIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Paper>
    );
}

export default MisLicencias;
